<template>
  <v-card>
    <v-expansion-panels flat accordion v-model="mainPanelIndex">
      <v-expansion-panel>
        <v-expansion-panel-header
          class="pa-0 profile-data__main-header"
          hide-actions
        >
          <v-card
            color="primaryDark"
            min-height="55px"
            width="100%"
            dark
            flat
            class="pa-2 subtitle-1"
          >
            <v-row no-gutters align="center" class="pr-2">
              <span>Profile Data</span>
              <v-spacer></v-spacer>
              <v-chip
                small
                outlined
                color="white"
                text-color="white"
                class="mr-2"
              >
                {{ configuredProfileCount }} / {{ panelItems.length }} configured
              </v-chip>
              <v-icon small>
                {{ mainPanelIndex === 0 ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </v-row>
          </v-card>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-card-text>
            <div v-if="configuredItems.length > 0" class="profile-data__summary">
              <v-chip
                v-for="item in configuredItems"
                :key="item.profileId"
                small
                class="mr-2 mb-2"
                outlined
              >
                {{ item.profileName }}: {{ identifiersSummary(item) }}
              </v-chip>
            </div>

            <v-alert v-if="panelError" type="error" outlined dense>
              {{ panelError }}
            </v-alert>

            <div v-else-if="panelLoading" class="profile-data__loading">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="caption mt-2">Loading profile data...</div>
            </div>

            <v-alert v-else-if="panelItems.length === 0" type="info" outlined dense>
              No profiles have been configured yet.
            </v-alert>

            <v-expansion-panels v-else v-model="expandedPanels" multiple>
              <v-expansion-panel
                v-for="item in panelItems"
                :key="item.profileId"
              >
                <v-expansion-panel-header>
                  <div class="profile-data__header">
                    <div class="profile-data__title-group">
                      <div class="profile-data__title">{{ item.profileName }}</div>
                      <div
                        v-if="item.profileDescription"
                        class="caption grey--text text--darken-1"
                      >
                        {{ item.profileDescription }}
                      </div>
                    </div>

                    <div class="profile-data__status-group">
                      <v-chip
                        x-small
                        outlined
                        :color="statusColor(item)"
                        class="mr-2"
                      >
                        {{ statusLabel(item) }}
                      </v-chip>
                      <v-chip v-if="hasIdentifiers(item)" x-small outlined>
                        {{ identifierCount(item) }} identifier(s)
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" :md="readOnly ? 12 : 8">
                      <div v-if="readOnly">
                        <div class="profile-data__field-label">Identifiers</div>
                        <div v-if="hasIdentifiers(item)" class="profile-data__identifiers-list">
                          <v-chip
                            v-for="identifier in item.identifiers"
                            :key="identifier.normalized"
                            small
                            outlined
                            color="primary"
                            class="mr-2 mb-2"
                          >
                            {{ identifier.value }}
                            <v-icon
                              v-if="isDefaultIdentifier(item, identifier)"
                              small
                              class="profile-data__default-icon ml-1"
                            >
                              mdi-star
                            </v-icon>
                          </v-chip>
                        </div>
                        <div v-else class="profile-data__empty">
                          No identifiers have been set for this profile yet.
                        </div>
                      </div>

                      <div v-else>
                        <div class="profile-data__field-label">Identifiers</div>

                        <!-- Existing identifiers as removable chips -->
                        <div v-if="hasIdentifiers(item)" class="profile-data__chip-group mb-3">
                          <v-chip
                            v-for="identifier in item.identifiers"
                            :key="identifier.normalized"
                            small
                            outlined
                            color="primary"
                            class="mr-2 mb-2"
                            :loading="isIdentifierSaving(item.profileId, normalizedIdentifierValue(identifier))"
                            :disabled="isIdentifierSaving(item.profileId, normalizedIdentifierValue(identifier))"
                            @click:close="removeIdentifier(item, identifier)"
                            close
                          >
                            {{ identifier.value }}
                            <v-icon
                              small
                              class="profile-data__default-icon ml-1"
                              :color="isDefaultIdentifier(item, identifier) ? 'amber darken-2' : 'grey darken-1'"
                              @click.stop="setDefaultIdentifier(item, identifier)"
                            >
                              {{ isDefaultIdentifier(item, identifier) ? "mdi-star" : "mdi-star-outline" }}
                            </v-icon>
                          </v-chip>
                        </div>

                        <!-- Add new identifier input -->
                        <v-row align="center" class="profile-data__add-identifier-row">
                          <v-col cols="12" :sm="8" class="profile-data__add-input-col">
                            <v-text-field
                              v-model="newIdentifiers[item.profileId]"
                              label="Add new identifier"
                              dense
                              outlined
                              clearable
                              hide-details="auto"
                              :disabled="isProfileSaving(item.profileId)"
                              @keydown.enter.prevent="addIdentifier(item)"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" :sm="4" class="profile-data__add-button-col">
                            <v-btn
                              color="primary"
                              small
                              block
                              class="profile-data__add-button"
                              :loading="isProfileSaving(item.profileId)"
                              :disabled="!canAddIdentifier(item)"
                              @click="addIdentifier(item)"
                            >
                              <v-icon left small>mdi-plus</v-icon>Add
                            </v-btn>
                          </v-col>
                        </v-row>

                        <!-- Error display for this profile -->
                        <div
                          v-if="saveErrorsByProfileId[item.profileId]"
                          class="error--text caption mt-2"
                        >
                          {{ saveErrorsByProfileId[item.profileId] }}
                        </div>

                      </div>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "AssetProfileDataPanel",
  props: {
    assetId: {
      default: "",
      type: String,
    },
    readOnly: {
      default: true,
      type: Boolean,
    },
  },
  data: () => ({
    panelLoading: false,
    panelError: null,
    mainPanelIndex: 0,
    newIdentifiers: {},
    savingByIdentifier: {}, // profileId_normalizedValue -> boolean
    saveErrorsByProfileId: {},
    expandedPanels: [],
  }),
  computed: {
    ...mapState("assetxpstate", ["profiles", "assetProfileData"]),
    panelItems() {
      const profiles = Array.isArray(this.profiles) ? this.profiles : [];
      const assetProfileData = Array.isArray(this.assetProfileData)
        ? this.assetProfileData
        : [];
      const recordsByProfileId = assetProfileData.reduce((lookup, item) => {
        lookup[item.profileId] = item;
        return lookup;
      }, {});

      const knownProfiles = profiles.map((profile) => {
        const existingRecord = recordsByProfileId[profile.id] || {};
        return {
          profileId: profile.id,
          profileName: profile.name || "Untitled Profile",
          profileDescription: profile.description || "",
          identifiers: this.sortIdentifiers(existingRecord.identifiers || []),
        };
      });

      const orphanedProfiles = assetProfileData
        .filter((item) => !profiles.some((profile) => profile.id === item.profileId))
        .map((item) => ({
          profileId: item.profileId,
          profileName: "Unknown Profile",
          profileDescription: "This profile record exists but the profile was not returned.",
          identifiers: this.sortIdentifiers(item.identifiers || []),
        }));

      return knownProfiles
        .concat(orphanedProfiles)
        .sort((left, right) => left.profileName.localeCompare(right.profileName));
    },
    configuredItems() {
      return this.panelItems.filter((item) => this.hasIdentifiers(item));
    },
    configuredProfileCount() {
      return this.configuredItems.length;
    },
  },
  watch: {
    assetId: {
      immediate: true,
      handler() {
        this.loadPanelData();
      },
    },
    panelItems() {
      this.syncNewIdentifierDrafts();
      this.syncExpandedPanels();
    },
  },
  methods: {
    ...mapActions("assetxpstate", [
      "GetProfiles",
      "GetAssetProfileData",
      "AddProfileIdentifier",
      "RemoveProfileIdentifier",
      "SetDefaultProfileIdentifier",
    ]),
    hasIdentifiers(item) {
      return Array.isArray(item.identifiers) && item.identifiers.length > 0;
    },
    identifierCount(item) {
      return Array.isArray(item.identifiers) ? item.identifiers.length : 0;
    },
    normalizedIdentifierValue(identifier) {
      if (!identifier) {
        return "";
      }

      const normalized = (identifier.normalized || "").trim().toLowerCase();
      if (normalized) {
        return normalized;
      }

      return (identifier.value || "").trim().toLowerCase();
    },
    isDefaultIdentifier(item, identifier) {
      if (!identifier || !item) {
        return false;
      }

      if (identifier.isDefault === true) {
        return true;
      }

      return this.identifierCount(item) === 1;
    },
    identifiersSummary(item) {
      if (!this.hasIdentifiers(item)) {
        return "(no identifiers)";
      }

      const count = this.identifierCount(item);
      if (count === 1) {
        return item.identifiers[0].value;
      }

      return `${item.identifiers.map(id => id.value).join(", ")}`;
    },
    sortIdentifiers(identifiers) {
      const list = Array.isArray(identifiers) ? identifiers.slice() : [];

      return list.sort((left, right) => {
        const leftDefault = left && left.isDefault === true ? 1 : 0;
        const rightDefault = right && right.isDefault === true ? 1 : 0;
        if (leftDefault !== rightDefault) {
          return rightDefault - leftDefault;
        }

        const leftOrder = Number.isInteger(left && left.order) ? left.order : 999;
        const rightOrder = Number.isInteger(right && right.order) ? right.order : 999;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }

        const leftValue = String((left && left.value) || "");
        const rightValue = String((right && right.value) || "");
        return leftValue.localeCompare(rightValue);
      });
    },
    statusColor(item) {
      if (this.isProfileSaving(item.profileId)) {
        return "info";
      }

      if (this.saveErrorsByProfileId[item.profileId]) {
        return "error";
      }

      return this.hasIdentifiers(item) ? "success" : "grey";
    },
    statusLabel(item) {
      if (this.isProfileSaving(item.profileId)) {
        return "Saving";
      }

      if (this.saveErrorsByProfileId[item.profileId]) {
        return "Error";
      }

      return this.hasIdentifiers(item) ? "Configured" : "Empty";
    },
    savingKey(profileId, normalizedValue) {
      return `${profileId}_${normalizedValue}`;
    },
    isIdentifierSaving(profileId, normalizedValue) {
      return Boolean(this.savingByIdentifier[this.savingKey(profileId, normalizedValue)]);
    },
    isProfileSaving(profileId) {
      // Check if any identifier for this profile is being saved
      return Object.keys(this.savingByIdentifier).some(
        key => key.startsWith(profileId + "_") && this.savingByIdentifier[key]
      );
    },
    canAddIdentifier(item) {
      const draft = (this.newIdentifiers[item.profileId] || "").trim();
      return draft.length > 0 && !this.isProfileSaving(item.profileId);
    },
    syncNewIdentifierDrafts() {
      const nextDrafts = {};

      this.panelItems.forEach((item) => {
        if (!Object.prototype.hasOwnProperty.call(this.newIdentifiers, item.profileId)) {
          nextDrafts[item.profileId] = "";
        } else {
          nextDrafts[item.profileId] = this.newIdentifiers[item.profileId];
        }
      });

      this.newIdentifiers = nextDrafts;
    },
    syncExpandedPanels() {
      if (!this.readOnly) {
        return;
      }

      this.expandedPanels = this.panelItems
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => this.hasIdentifiers(item))
        .slice(0, 2)
        .map(({ index }) => index);
    },
    async loadPanelData() {
      if (!this.assetId) {
        this.panelError = null;
        this.panelLoading = false;
        return;
      }

      this.panelLoading = true;
      this.panelError = null;

      try {
        await Promise.all([
          this.GetProfiles(),
          this.GetAssetProfileData(this.assetId),
        ]);
      } catch (error) {
        console.error("[AssetProfileDataPanel] Failed to load profile data", error);
        this.panelError = "We could not load profile data for this asset.";
      } finally {
        this.panelLoading = false;
      }
    },
    async addIdentifier(item) {
      const draft = (this.newIdentifiers[item.profileId] || "").trim();
      if (!draft || !this.assetId) {
        return;
      }

      const profileId = item.profileId;
      const normalizedValue = draft.toLowerCase();
      const savingKey = this.savingKey(profileId, normalizedValue);

      this.$set(this.savingByIdentifier, savingKey, true);
      this.$delete(this.saveErrorsByProfileId, profileId);

      try {
        await this.AddProfileIdentifier({
          assetId: this.assetId,
          profileId,
          identifierValue: draft,
        });

        // Clear the draft input on success
        this.$set(this.newIdentifiers, profileId, "");
      } catch (error) {
        console.error("[AssetProfileDataPanel] Failed to add identifier", error);
        this.$set(
          this.saveErrorsByProfileId,
          profileId,
          "The identifier could not be added. Please try again."
        );
      } finally {
        this.$delete(this.savingByIdentifier, savingKey);
      }
    },
    async removeIdentifier(item, identifier) {
      if (!this.assetId) {
        return;
      }

      const profileId = item.profileId;
      const normalizedValue = this.normalizedIdentifierValue(identifier);
      const savingKey = this.savingKey(profileId, normalizedValue);

      this.$set(this.savingByIdentifier, savingKey, true);
      this.$delete(this.saveErrorsByProfileId, profileId);

      try {
        await this.RemoveProfileIdentifier({
          assetId: this.assetId,
          profileId,
          identifierValue: identifier.value,
        });
      } catch (error) {
        console.error("[AssetProfileDataPanel] Failed to remove identifier", error);
        this.$set(
          this.saveErrorsByProfileId,
          profileId,
          "The identifier could not be removed. Please try again."
        );
      } finally {
        this.$delete(this.savingByIdentifier, savingKey);
      }
    },
    async setDefaultIdentifier(item, identifier) {
      if (!this.assetId || !item || !identifier || this.isDefaultIdentifier(item, identifier)) {
        return;
      }

      const profileId = item.profileId;
      const normalizedValue = this.normalizedIdentifierValue(identifier);
      const savingKey = this.savingKey(profileId, normalizedValue);

      this.$set(this.savingByIdentifier, savingKey, true);
      this.$delete(this.saveErrorsByProfileId, profileId);

      try {
        await this.SetDefaultProfileIdentifier({
          assetId: this.assetId,
          profileId,
          identifierValue: identifier.value,
        });
      } catch (error) {
        console.error("[AssetProfileDataPanel] Failed to set default identifier", error);
        this.$set(
          this.saveErrorsByProfileId,
          profileId,
          "The default identifier could not be updated. Please try again."
        );
      } finally {
        this.$delete(this.savingByIdentifier, savingKey);
      }
    },
  },
};
</script>

<style scoped>
.profile-data__summary {
  margin-bottom: 16px;
}

.profile-data__main-header {
  padding: 0 !important;
  min-height: 0 !important;
}

.profile-data__main-header.v-expansion-panel-header--active {
  min-height: 0 !important;
}

.profile-data__loading {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  min-height: 120px;
}

.profile-data__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.profile-data__title-group {
  flex: 1;
}

.profile-data__title {
  font-weight: 500;
  font-size: 1rem;
}

.profile-data__status-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-data__field-label {
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 8px;
}

.profile-data__field-value {
  font-size: 0.95rem;
}

.profile-data__empty {
  color: #999;
  font-style: italic;
}

.profile-data__identifiers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.profile-data__chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.profile-data__add-identifier-row {
  margin-top: 8px;
}

.profile-data__add-input-col {
  display: flex;
  align-items: center;
}

.profile-data__add-button-col {
  display: flex;
  align-items: center;
}

.profile-data__add-button {
  min-height: 40px;
}

.profile-data__default-icon {
  cursor: pointer;
}
</style>
